<script setup>
import { ref, computed, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import agentAPI from 'dashboard/api/agents';

const store = useStore();
const agents = useMapGetter('agents/getAgents');

const selectedAgentId = ref(null);
const accessHoursEnabled = ref(false);
const accessHours = ref({});
const isSaving = ref(false);

const DAYS = [
  { key: '1', label: 'Segunda-feira' },
  { key: '2', label: 'Terça-feira' },
  { key: '3', label: 'Quarta-feira' },
  { key: '4', label: 'Quinta-feira' },
  { key: '5', label: 'Sexta-feira' },
  { key: '6', label: 'Sábado' },
  { key: '0', label: 'Domingo' },
];

const agentOptions = computed(() =>
  (agents.value || []).filter(a => a.role !== 'administrator')
);

const selectedAgent = computed(() =>
  (agents.value || []).find(a => a.id === selectedAgentId.value)
);

const initDefaultHours = () => {
  const defaults = {};
  DAYS.forEach(d => {
    defaults[d.key] = {
      enabled: d.key !== '0' && d.key !== '6',
      from: '08:00',
      to: '18:00',
    };
  });
  return defaults;
};

const loadAgentHours = () => {
  if (!selectedAgent.value) return;
  accessHoursEnabled.value = selectedAgent.value.access_hours_enabled || false;
  const hours = selectedAgent.value.access_hours;
  if (hours && Object.keys(hours).length > 0) {
    // Merge with defaults in case some days are missing
    const defaults = initDefaultHours();
    DAYS.forEach(d => {
      if (hours[d.key]) {
        defaults[d.key] = { ...defaults[d.key], ...hours[d.key] };
      }
    });
    accessHours.value = defaults;
  } else {
    accessHours.value = initDefaultHours();
  }
};

watch(selectedAgentId, loadAgentHours);

const saveAccessHours = async () => {
  if (!selectedAgentId.value) return;
  isSaving.value = true;
  try {
    await agentAPI.update(selectedAgentId.value, {
      access_hours_enabled: accessHoursEnabled.value,
      access_hours: accessHours.value,
    });
    // Update store
    await store.dispatch('agents/get');
    useAlert('Horário de acesso salvo com sucesso!');
  } catch (error) {
    useAlert(error.message || 'Erro ao salvar horário de acesso.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div>
      <h2 class="text-lg font-semibold text-n-slate-12 mb-1">
        Controle de Horário de Acesso
      </h2>
      <p class="text-sm text-n-slate-10">
        Configure os horários em que cada agente pode acessar a plataforma.
        Fora do horário configurado, o acesso será bloqueado.
      </p>
    </div>

    <div>
      <label class="text-sm font-medium text-n-slate-12 mb-1 block">
        Selecionar agente
      </label>
      <select
        v-model="selectedAgentId"
        class="w-full max-w-md px-3 py-2 border border-n-weak rounded-lg text-sm bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option :value="null" disabled>Escolha um agente...</option>
        <option
          v-for="agent in agentOptions"
          :key="agent.id"
          :value="agent.id"
        >
          {{ agent.name }} ({{ agent.email }})
        </option>
      </select>
    </div>

    <div v-if="selectedAgent" class="flex flex-col gap-4">
      <div class="flex items-center gap-3">
        <label class="relative inline-flex items-center cursor-pointer">
          <input
            v-model="accessHoursEnabled"
            type="checkbox"
            class="sr-only peer"
          />
          <div
            class="w-11 h-6 bg-n-alpha-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-blue-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-n-weak after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-n-blue-9"
          />
        </label>
        <div>
          <span class="text-sm font-medium text-n-slate-12">
            Ativar controle de horário
          </span>
          <p class="text-xs text-n-slate-10">
            Quando ativado, {{ selectedAgent.name }} só poderá acessar a
            plataforma nos horários configurados abaixo.
          </p>
        </div>
      </div>

      <div
        v-if="accessHoursEnabled"
        class="border border-n-weak rounded-xl overflow-hidden"
      >
        <div
          class="grid grid-cols-[1fr_120px_120px_48px] gap-2 px-4 py-2 bg-n-alpha-1 text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
        >
          <span>Dia</span>
          <span class="text-center">Início</span>
          <span class="text-center">Fim</span>
          <span class="text-center">Ativo</span>
        </div>
        <div
          v-for="day in DAYS"
          :key="day.key"
          class="grid grid-cols-[1fr_120px_120px_48px] gap-2 items-center px-4 py-2 border-t border-n-weak"
          :class="!accessHours[day.key]?.enabled ? 'opacity-50' : ''"
        >
          <span class="text-sm text-n-slate-12">{{ day.label }}</span>
          <input
            v-model="accessHours[day.key].from"
            type="time"
            class="px-2 py-1 border border-n-weak rounded text-xs bg-transparent text-n-slate-12 text-center"
            :disabled="!accessHours[day.key]?.enabled"
          />
          <input
            v-model="accessHours[day.key].to"
            type="time"
            class="px-2 py-1 border border-n-weak rounded text-xs bg-transparent text-n-slate-12 text-center"
            :disabled="!accessHours[day.key]?.enabled"
          />
          <div class="flex justify-center">
            <input
              v-model="accessHours[day.key].enabled"
              type="checkbox"
              class="w-4 h-4 accent-blue-500 cursor-pointer"
            />
          </div>
        </div>
      </div>

      <div>
        <button
          class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg cursor-pointer disabled:opacity-50"
          style="background: #3b82f6; border: none"
          :disabled="isSaving"
          @click="saveAccessHours"
        >
          <span
            v-if="isSaving"
            class="i-lucide-loader-2 size-4 animate-spin"
          />
          Salvar horário de acesso
        </button>
      </div>
    </div>
  </div>
</template>
